import { useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";

import { api360 } from "./api";
import { usePermissionStore } from "../stores/permissionStore";

export type CampaignSearchRequest = {
  campaignName?: string;
  campaignID?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
  pagination: {
    currentPage: number;
    pageSize?: number;
    total?: number;
    totalPages?: number;
  };
  permission_Token?: string;
};

export const useSearchCampaign = (
  data: CampaignSearchRequest,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: ["searchCampaign", data],
    queryFn: () => getCampaignDetails(data),
    enabled,
    retry: false,
  });
};

export const getCampaignDetails = async (
  data: CampaignSearchRequest,
): Promise<AxiosResponse> => {
  const url = "/c360/v1/api/campaign/campaign-details";

  try {
    const response = await api360.post(url, {
      permission_Token: usePermissionStore.getState().permissionToken ?? "",
    });

    const {
      campaignName,
      campaignID,
      startDate,
      endDate,
      status,
      pagination: { currentPage, pageSize = 10 },
    } = data;

    const rawData = response.data?.campaignDetails || [];

    const filteredData = rawData.filter((item: any) => {
      const matchName = campaignName
        ? item.campaignName?.toLowerCase().includes(campaignName.toLowerCase())
        : true;

      const matchID = campaignID
        ? `${item.campaignID}`.includes(`${campaignID}`)
        : true;

      const matchStatus = status
        ? item.status?.toLowerCase() === status.toLowerCase()
        : true;

      const matchStartDate = startDate
        ? new Date(item.startDateTime) >= new Date(startDate)
        : true;

      const matchEndDate = endDate
        ? new Date(item.campaignEndDateTime) <= new Date(endDate)
        : true;

      return (
        matchName && matchID && matchStatus && matchStartDate && matchEndDate
      );
    });

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = filteredData.slice(startIndex, endIndex);

    return {
      ...response,
      data: {
        campaignDetails: paginatedData,
        pagination: {
          currentPage,
          pageSize,
          total: rawData?.length || 0,
          totalPages: Math.ceil(filteredData.length / pageSize),
        },
      },
    };
  } catch (error) {
    console.error("Error fetching campaign details:", error);
    throw error;
  }
};

export type CampaignSummaryRequest = {
  status?: string[];
  permission_Token: string;
};

export type CampaignSummaryResponse = {
  totalCount: number;
  sentCount: number;
  engagedCount: number;
  status: string[];
  rate: number;
  country: string;
};

export const getCampaignSummary = async (
  data: CampaignSummaryRequest,
): Promise<AxiosResponse<CampaignSummaryResponse>> => {
  const url = "/c360/v1/api/campaign/getsummary";

  try {
    const response = await api360.post(url, data);
    return {
      ...response,
      data: response.data as CampaignSummaryResponse,
    };
  } catch (error) {
    console.error("Error fetching campaign summary:", error);
    throw error;
  }
};

export const useGetCampaignSummary = (
  data: CampaignSummaryRequest,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: ["campaignSummary", data],
    queryFn: () => getCampaignSummary(data),
    enabled,
    retry: false,
  });
};
